import { Injectable, NgZone, ViewContainerRef, inject } from '@angular/core';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { firstValueFrom } from 'rxjs';
import {
  OverlayConfirmationComponent,
  OverlayConfirmationContext,
  OverlayConfirmationResult,
} from '../../../Modules/shared/confirmation-code/overlay-confirmation/overlay-confirmation.component';
import { ConfirmationService } from '../confirmation-service/confirmation-service';
import { ViewContainerRefService } from '../view-container-ref/view-container-ref.service';
import { ConfirmationDeclinedError } from './confirmation-declined-error';

/**
 * Overlay-hosted variant of the /confirmation-code SCA flow. Unlike
 * ConfirmationService.confirmAction (which router-navigates to
 * ConfirmationCodeComponent, destroying the current page), confirm() renders
 * OverlayConfirmationComponent in a BrnDialog above the current page — no
 * router navigation, so the hosting page (e.g. a custom-widget iframe)
 * survives — and reports the outcome explicitly:
 *
 * - Resolves `{ token }` — the Keycloak confirmation access_token minted by
 *   KeycloakCustomService.sendConfirmation; callers attach it as the
 *   `confirmation` HTTP header on the gated request.
 * - Rejects with ConfirmationDeclinedError (code 'confirmation-declined',
 *   reason 'cancelled' | 'timeout') when the user cancels or the 120s
 *   timeout fires. Any other rejection is an infrastructure failure
 *   (preference resolution / dialog hosting), not a decline.
 *
 * Adapting to the custom-widget ConfirmationRunner contract
 * (Modules/shared/widgets/custom-widget/custom-widget-confirmation.ts) — the
 * runner adapter should map outcomes like this:
 *
 *   const runner: ConfirmationRunner = {
 *     run: (claimType, operation) =>
 *       overlayConfirmationService.confirm(claimType, operation).catch((error) => {
 *         if (error instanceof ConfirmationDeclinedError) {
 *           throw new HostError('confirmation-declined', error.message);
 *         }
 *         throw new HostError('host-error', `confirmation-failed: ${error?.message ?? error}`);
 *       }),
 *   };
 */
@Injectable({ providedIn: 'root' })
export class OverlayConfirmationService {
  private readonly dialogService = inject(BrnDialogService);
  private readonly rootViewContainerRef = inject(ViewContainerRefService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly zone = inject(NgZone);

  public async confirm(claimType: string, reason?: string): Promise<{ token: string }> {
    // Same preference resolution as ConfirmationService.confirmAction.
    const credentialType = await this.confirmationService.resolvePreferredCredentialType();
    return this.zone.run(async () => {
      // BrnDialogService falls back to its own injector when no VCR is
      // available (e.g. before AppComponent registers the root one).
      let vcr: ViewContainerRef | undefined;
      try {
        vcr = this.rootViewContainerRef.get();
      } catch {
        vcr = undefined;
      }
      const context: OverlayConfirmationContext = { claimType, credentialType, reason };
      const dialogRef = this.dialogService.open(OverlayConfirmationComponent, vcr, context, {
        role: 'alertdialog',
        // Decline must be explicit (cancel button or timeout) so the caller
        // always gets a distinguishable outcome; esc/backdrop must not
        // silently dismiss.
        disableClose: true,
        closeOnBackdropClick: false,
        closeOnOutsidePointerEvents: false,
      });

      const result: OverlayConfirmationResult | undefined = await firstValueFrom(dialogRef.closed$);
      if (result && 'token' in result) {
        return { token: result.token };
      }
      throw new ConfirmationDeclinedError(
        result && 'declined' in result ? result.declined : 'cancelled',
        claimType,
        reason,
      );
    });
  }
}
